
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import {
  IWebhookProviderData,
  IWebhookProviderMethods
} from "@/models/providers/webhooks";
import { IState } from "@/store";
import { IWebhook } from "@/models/webhooks";

@Component({
  computed: {
    ...mapState({
      webhookScope(state: IState, getters) {
        return getters[`data/${DataModules.WEBHOOKS}/scope`](this.webhookId);
      },
      webhook(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.WEBHOOKS,
          scope: this.webhookScope
        });
      }
    })
  }
})
export default class WebhookProvider extends Mixins(RequestPromises) {
  // Map State

  public webhook!: IWebhook;
  public webhookScope!: string;

  // Props

  @Prop({ required: true })
  public webhookId!: IWebhook["id"];

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.WEBHOOKS,
      scope: this.webhookScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getWebhook();
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getWebhook() {
    return this.$store.dispatch(`data/${DataModules.WEBHOOKS}/get`, {
      id: this.webhookId,
      scope: this.webhookScope,
      vm: this,
      ignoreStored: this.isReloading
    });
  }

  public updateWebhook(data) {
    if (!this.canUpdate) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.WEBHOOKS}/update`, {
      id: this.webhookId,
      data
    });
  }

  // Computed

  public get canUpdate() {
    return this.$userCan("create_webhook");
  }

  // Guarding

  public get guardFunctionalities() {
    return ["get_webhook"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("webhookProviderData")
  public webhookProviderData(): IWebhookProviderData {
    return {
      canUpdate: this.canUpdate,
      guardFunctionalities: this.guardFunctionalities,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      webhook: this.webhook
    };
  }

  @Provide("webhookProviderMethods")
  public get webhookProviderMethods(): IWebhookProviderMethods {
    return {
      reloadData: this.reloadData,
      updateWebhook: this.updateWebhook
    };
  }

  // Render function
  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $webhookData: this.webhookProviderData(),
        $webhookMethods: this.webhookProviderMethods
      })
    );
  }
}
